import React, { useMemo } from 'react';
import { Breadcrumbs, Link, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ChevronRight } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { customerPath, projectPath, sessionPath, templateEditorPath } from '../../utils/navigation';

const SECTION_LABELS = {
  sessions: 'Sessions',
  requirements: 'Requirements',
  documents: 'Documents',
  context: 'Context',
};

/**
 * Resolve the Customer > Project > Section > Leaf trail for the given ids from AppContext.
 * Unknown ids degrade gracefully (the leaf still shows, with `current` or a generic label).
 * @param {object} params - Ids and labels.
 * @param {Array<object>} params.projects - AppContext projects.
 * @param {Array<object>} params.customers - AppContext customers.
 * @param {Array<object>} params.templates - AppContext templates.
 * @param {string} [params.customerId] - Customer id.
 * @param {string} [params.projectId] - Project id.
 * @param {string} [params.sessionId] - Session id.
 * @param {string} [params.documentId] - Document id.
 * @param {string} [params.templateId] - Template id.
 * @param {'sessions'|'requirements'|'documents'|'context'} [params.section] - Project section crumb.
 * @param {string} [params.current] - Leaf label override.
 * @param {Array<{label: string, to?: string}>} [params.extra] - Extra crumbs before the leaf.
 * @returns {Array<{key: string, label: string, to?: string}>} Crumbs (last one is the current page).
 */
export function buildEntityTrail({ projects = [], customers = [], templates = [], customerId, projectId, sessionId, documentId, templateId, section, current, extra = [] }) {
  const crumbs = [];

  if (templateId) {
    const template = templates.find((t) => t.id === templateId);
    crumbs.push({ key: 'templates', label: 'Templates', to: '/templates' });
    crumbs.push({ key: `template-${templateId}`, label: current || template?.name || 'Template', to: templateEditorPath({ templateId, kind: template?.templateType }) });
    return crumbs;
  }

  let project = projectId ? projects.find((p) => p.id === projectId) || null : null;
  let document = null;
  let session = null;
  if (documentId) {
    for (const p of projects) {
      const found = (p.generatedDocuments || []).find((d) => d.id === documentId);
      if (found) {
        document = found;
        if (!project) project = p;
        break;
      }
    }
  }
  if (sessionId) {
    for (const p of projects) {
      const found = (p.sessions || []).find((s) => s.id === sessionId);
      if (found) {
        session = found;
        if (!project) project = p;
        break;
      }
    }
  }
  const resolvedCustomerId = customerId || project?.customer_id || null;
  const customer = resolvedCustomerId
    ? customers.find((c) => c.id === resolvedCustomerId) || project?.customer || null
    : null;

  if (customer) {
    crumbs.push({ key: 'customers', label: 'Customers', to: '/customers' });
    crumbs.push({ key: `customer-${customer.id}`, label: customer.name || 'Customer', to: customerPath(customer.id) });
  } else if (project || projectId) {
    crumbs.push({ key: 'projects', label: 'Projects', to: '/projects' });
  } else if (sessionId) {
    crumbs.push({ key: 'recordings', label: 'Recordings', to: '/customers/sessions' });
  } else if (documentId) {
    crumbs.push({ key: 'documents', label: 'Documents', to: '/documents' });
  }

  const pid = project?.id || projectId;
  if (pid) {
    crumbs.push({ key: `project-${pid}`, label: project?.name || 'Project', to: projectPath(pid) });
    const sectionKey = section || (documentId ? 'documents' : sessionId ? 'sessions' : null);
    if (sectionKey && SECTION_LABELS[sectionKey]) {
      crumbs.push({ key: `section-${sectionKey}`, label: SECTION_LABELS[sectionKey], to: projectPath(pid, sectionKey) });
    }
  }

  extra.forEach((item, index) => crumbs.push({ key: `extra-${index}`, ...item }));

  if (documentId) crumbs.push({ key: `document-${documentId}`, label: current || document?.name || 'Document' });
  else if (sessionId) crumbs.push({ key: `session-${sessionId}`, label: current || session?.name || 'Recording', to: sessionPath(sessionId) });
  else if (current) crumbs.push({ key: 'current', label: current });

  return crumbs;
}

/**
 * Unified breadcrumbs: Customers > Customer > Project > Section > Current, resolved from
 * AppContext by id. Use on project, recording, document and customer pages.
 *
 * Props: customerId, projectId, sessionId, documentId, templateId (ids; pass what the route
 * has), section ('sessions'|'requirements'|'documents'|'context'), current (leaf label),
 * extra ([{label, to}] crumbs inserted before the leaf), sx.
 * The last crumb is rendered as the current page (aria-current="page"), not a link.
 * @param {object} props - Props (see buildEntityTrail).
 * @returns {JSX.Element|null} Breadcrumbs.
 */
export default function EntityBreadcrumbs({ customerId, projectId, sessionId, documentId, templateId, section, current, extra, sx }) {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const { state } = useApp();
  const crumbs = useMemo(
    () => buildEntityTrail({
      projects: state.projects,
      customers: state.customers,
      templates: state.templates,
      customerId,
      projectId,
      sessionId,
      documentId,
      templateId,
      section,
      current,
      extra,
    }),
    [state.projects, state.customers, state.templates, customerId, projectId, sessionId, documentId, templateId, section, current, extra]
  );
  if (!crumbs.length) return null;

  return (
    <Breadcrumbs
      aria-label="Breadcrumb"
      maxItems={compact ? 3 : 8}
      itemsBeforeCollapse={1}
      itemsAfterCollapse={compact ? 1 : 2}
      separator={<ChevronRight sx={{ fontSize: 15, color: 'var(--q-faint, #716C65)' }} />}
      sx={{
        fontSize: 13,
        lineHeight: 1.4,
        minWidth: 0,
        '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
        '& .MuiBreadcrumbs-li': { minWidth: 0 },
        '& .MuiBreadcrumbs-separator': { mx: 0.5 },
        ...sx,
      }}
    >
      {crumbs.map((crumb, index) => {
        const last = index === crumbs.length - 1;
        if (last || !crumb.to) {
          return (
            <Typography
              key={crumb.key}
              component="span"
              aria-current={last ? 'page' : undefined}
              noWrap
              title={crumb.label}
              sx={{ display: 'block', fontSize: 'inherit', fontWeight: last ? 500 : 400, color: last ? 'var(--q-text, #1C1B19)' : 'var(--q-muted, #5E5A54)', maxWidth: { xs: 160, sm: 280 } }}
            >
              {crumb.label}
            </Typography>
          );
        }
        return (
          <Link
            key={crumb.key}
            component={RouterLink}
            to={crumb.to}
            underline="hover"
            title={crumb.label}
            sx={{ display: 'block', color: 'var(--q-muted, #5E5A54)', fontSize: 'inherit', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: { xs: 120, sm: 200 }, '&:hover': { color: 'var(--q-text, #1C1B19)' } }}
          >
            {crumb.label}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
}

/**
 * Small "Back to ..." target derived from the same trail (the closest linked ancestor).
 * @param {Array<{to?: string}>} crumbs - Trail from buildEntityTrail.
 * @returns {string|null} Path.
 */
export function parentPathFromTrail(crumbs) {
  for (let i = crumbs.length - 2; i >= 0; i -= 1) {
    if (crumbs[i].to) return crumbs[i].to;
  }
  return null;
}

